import React from 'react';
import { Flex, Box, Heading, Text } from '@chakra-ui/react';
import { StepperSidebar } from 'src/features/Signup/components/organisms/StepperSidebar';
import { Button } from 'src/components/atoms/Button';

type PageContainerProps = {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  currentStep?: number;
  onNext?: () => void;
  onBack?: () => void;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isFormValid?: boolean;
  hideStepper?: boolean;
  submitLabel?: string;
  nextLabel?: string;
};

export const PageContainer: React.FC<PageContainerProps> = ({
  title,
  subtitle,
  children,
  currentStep,
  onNext,
  onBack,
  isBackDisabled = false,
  isNextDisabled = false,
  isFormValid,
  hideStepper = false,
  submitLabel = 'Create account',
  nextLabel,
}) => {
  return (
    <Box
      minH="100vh"
      bg={hideStepper ? 'surface.panel' : 'surface.page'}
      px={0}
      py={0}>
      <Box
        mx="auto"
        pt={{ base: '96px', md: '112px' }}
        pb={10}
        px={{ base: 4, md: 8, lg: 10 }}>
        {hideStepper && (
          <Box
            mx="auto"
            mt={{ base: 8, md: 24 }}
            w={{ base: '100%', xl: '75%' }}>
            <Heading
              as="h1"
              size="xl"
              mb={4}
              color="content.title"
              fontWeight={700}
              fontFamily="marketingPage">
              {title}
            </Heading>
            <Text color="content.muted" mb={8} fontSize="16px">
              {subtitle}
            </Text>
          </Box>
        )}
        {hideStepper ? (
          <Box
            mx="auto"
            w={{ base: '100%', xl: '75%' }}
            minW={0}
            overflowX="auto">
            {children}
            <Flex mt={8} justify="flex-end">
              <Button
                buttonName={submitLabel}
                bg="icon.green"
                color="white"
                borderRadius="lg"
                py={3.5}
                px={10}
                fontWeight={600}
                fontSize="lg"
                boxShadow="none"
                _hover={{ bg: 'icon.green', opacity: 0.9 }}
                onClick={onNext}
                isDisabled={isFormValid === false}>
                {submitLabel}
              </Button>
            </Flex>
          </Box>
        ) : (
          <Flex
            gap={{ base: 4, xl: 8 }}
            align="flex-start"
            justify={{ base: 'center', xl: 'space-between' }}
            direction={{ base: 'column', xl: 'row-reverse' }}>
            <Box
              flex={1}
              w={{ base: '100%', xl: 'auto' }}
              minW={0}
              overflowX="auto"
              bg="surface.panel"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="lg"
              p={{ base: 4, md: 6 }}>
              {children}
            </Box>
            <Box
              w={{ base: '100%', xl: '360px' }}
              flexShrink={0}
              position={{ base: 'static', xl: 'sticky' }}
              top={{ base: 'auto', xl: '112px' }}
              alignSelf="flex-start">
              <StepperSidebar
                title={title}
                subtitle={subtitle}
                currentStep={currentStep ?? 0}
                onNext={onNext}
                onBack={onBack}
                isBackDisabled={isBackDisabled}
                isNextDisabled={isNextDisabled}
                isFormValid={isFormValid}
                nextLabel={nextLabel}
              />
            </Box>
          </Flex>
        )}
      </Box>
    </Box>
  );
};
